/**
 * JSON with object keys ordered at every depth, so two structurally identical
 * documents serialize identically no matter what order their keys were built in.
 * This is what makes a content hash a statement about content rather than about
 * the code path that assembled it.
 *
 * The string is written by hand rather than by sorting into an object literal:
 * a JavaScript object reorders integer-like keys ahead of the rest regardless of
 * insertion order, which would make the output depend on key spelling.
 *
 * Arrays keep their order — order carries meaning in every artifact that uses
 * this (question order, narrative order), so it belongs in the hash.
 */
const write = (value: unknown): string | undefined => {
  if (value === undefined || typeof value === 'function') {
    return undefined;
  }

  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    // An array slot cannot be elided, so a hole or an undefined entry is `null`,
    // matching what `JSON.stringify` does.
    return `[${value.map((entry) => write(entry) ?? 'null').join(',')}]`;
  }

  const parts: string[] = [];
  for (const [key, entry] of Object.entries(value).toSorted(([left], [right]) =>
    left < right ? -1 : left > right ? 1 : 0,
  )) {
    const encoded = write(entry);
    if (encoded !== undefined) {
      parts.push(`${JSON.stringify(key)}:${encoded}`);
    }
  }

  return `{${parts.join(',')}}`;
};

export const canonicalJson = (value: unknown): string => write(value) ?? 'null';
